/** @jsxImportSource react */
import { useEffect, useRef } from "react";
import * as d3 from "d3";
import type { MesaWithComparison } from "../lib/types";
import { mesaAggregateComparison } from "./ComparisonTable";
import { PARTY_DEFINITIONS } from "../../shared/lib/party-mappings";

interface SlopeChartProps {
  mesas: MesaWithComparison[];
  statMode: "median" | "average";
}

export function SlopeChart({ mesas, statMode }: SlopeChartProps) {
  const comparison = mesaAggregateComparison(mesas, statMode);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current) return;

    // Configuración
    const config = {
      width: 1200,
      height: 600,
      marginTop: 40,
      marginRight: 120,
      marginBottom: 10,
      marginLeft: 120,
      padding: 20,
      yearFontSize: "25px",
      labelFontSize: "25px",
      diffLabelFontSize: "16px",
      diffLabelStrokeWidth: 5,
      circleRadius: 9,
      lineWidth: 7,
      dodgeSeparation: 20,
      lineOffset: 12,
      diffLabelOffset: -14,
      diffLabelCollisionThreshold: 30,
      diffLabelHorizontalOffset: 55,
      numberFormat: {
        decimals: 1,
        decimalSeparator: ",",
        thousandsSeparator: ".",
        showPercentSign: true,
      },
    };

    // Datos derivados del agregado de mesas
    const chartData = [
      {
        key: "jxc",
        name: PARTY_DEFINITIONS["jxc"]!.displayName,
        2023: comparison.jxc!.pct2023,
        2025: comparison.jxc!.pct2025,
        color: PARTY_DEFINITIONS["jxc"]!.color,
      },
      {
        key: "provincias_unidas",
        name: "Scharetti", // Historical 2023 name
        2023: comparison.provincias_unidas!.pct2023,
        name2: PARTY_DEFINITIONS["provincias_unidas"]!.displayName, // 2025 name
        2025: comparison.provincias_unidas!.pct2025,
        color: PARTY_DEFINITIONS["provincias_unidas"]!.color,
      },
      {
        key: "fp",
        name: "Frente de Todos", // Historical 2023 name
        2023: comparison.fp!.pct2023,
        name2: PARTY_DEFINITIONS["fp"]!.displayName, // 2025 name
        2025: comparison.fp!.pct2025,
        color: PARTY_DEFINITIONS["fp"]!.color,
      },
      {
        key: "lla",
        name: PARTY_DEFINITIONS["lla"]!.displayName,
        2023: comparison.lla!.pct2023,
        2025: comparison.lla!.pct2025,
        color: PARTY_DEFINITIONS["lla"]!.color,
      },
    ];

    const locale = d3.formatLocale({
      decimal: config.numberFormat.decimalSeparator,
      thousands: config.numberFormat.thousandsSeparator,
      grouping: [3],
      currency: ["$", ""],
    });

    const formatNumber = (value: number) => {
      const formatted = locale.format(`.${config.numberFormat.decimals}f`)(
        value,
      );
      return config.numberFormat.showPercentSign ? `${formatted}%` : formatted;
    };

    const formatNumberNoPct = (value: number) => {
      return locale.format(`.${config.numberFormat.decimals}f`)(value);
    };

    function dodge(
      positions: number[],
      separation = config.dodgeSeparation,
      maxiter = 10,
      maxerror = 1e-1,
    ) {
      positions = Array.from(positions);
      const n = positions.length;
      if (!positions.every(isFinite)) throw new Error("invalid position");
      if (!(n > 1)) return positions;
      const index = d3.range(positions.length) as number[];
      for (let iter = 0; iter < maxiter; ++iter) {
        index.sort((i, j) => d3.ascending(positions[i]!, positions[j]!));
        let error = 0;
        for (let i = 1; i < n; ++i) {
          const curr = index[i] as number;
          const prev = index[i - 1] as number;
          let delta = (positions[curr] as number) - (positions[prev] as number);
          if (delta < separation) {
            delta = (separation - delta) / 2;
            error = Math.max(error, delta);
            positions[prev] = (positions[prev] as number) - delta;
            positions[curr] = (positions[curr] as number) + delta;
          }
        }
        if (error < maxerror) break;
      }
      return positions;
    }

    function resolveDiffLabelCollisions(
      diffLabels: Array<{ x: number; y: number }>,
    ) {
      const offsets = new Array(diffLabels.length).fill(0);
      for (let i = 0; i < diffLabels.length; i++) {
        for (let j = i + 1; j < diffLabels.length; j++) {
          const a = diffLabels[i]!;
          const b = diffLabels[j]!;
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < config.diffLabelCollisionThreshold) {
            if (a.x < b.x) {
              offsets[i] = -config.diffLabelHorizontalOffset;
              offsets[j] = config.diffLabelHorizontalOffset;
            } else {
              offsets[i] = config.diffLabelHorizontalOffset;
              offsets[j] = -config.diffLabelHorizontalOffset;
            }
          }
        }
      }
      return offsets;
    }

    const x = d3
      .scalePoint<string>()
      .domain(["0", "1"])
      .range([config.marginLeft, config.width - config.marginRight])
      .padding(0.5);
    const y = d3
      .scaleLinear()
      .range([config.height - config.marginBottom, config.marginTop]);

    function createLine(d: any) {
      const x1 = x("0")!,
        y1 = y(d["2023"]),
        x2 = x("1")!,
        y2 = y(d["2025"]);
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const startX = x1 + config.lineOffset * Math.cos(angle);
      const startY = y1 + config.lineOffset * Math.sin(angle);
      const endX = x2 - config.lineOffset * Math.cos(angle);
      const endY = y2 - config.lineOffset * Math.sin(angle);
      return d3.line()([
        [startX, startY],
        [endX, endY],
      ]);
    }

    const svg = d3
      .select(svgRef.current)
      .attr("viewBox", [0, 0, config.width, config.height])
      .attr(
        "style",

        "max-width: 100%; height: 100%; margin:auto; font: 10px sans-serif;",
      );

    // Limpiar contenido previo
    svg.selectAll("*").remove();

    const baseLine = svg
      .append("line")
      .attr("x1", config.marginLeft)
      .attr("x2", config.width - config.marginRight)
      .attr("stroke", "#ccc")
      .attr("stroke-width", 1);

    svg
      .append("line")
      .attr("x1", x("0")!)
      .attr("x2", x("0")!)
      .attr("y1", config.marginTop)
      .attr("y2", config.height - config.marginBottom)
      .attr("stroke", "#ccc")
      .attr("stroke-width", 1)
      .attr("stroke-dasharray", "4,4");
    svg
      .append("line")
      .attr("x1", x("1")!)
      .attr("x2", x("1")!)
      .attr("y1", config.marginTop)
      .attr("y2", config.height - config.marginBottom)
      .attr("stroke", "#ccc")
      .attr("stroke-width", 1)
      .attr("stroke-dasharray", "4,4");

    const yearGroup = svg.append("g").attr("text-anchor", "middle");
    const years = yearGroup.selectAll("g").data(["0", "1"]).join("g");
    years
      .attr("transform", (i: string) => `translate(${x(i)},20)`)
      .append("text")
      .text((i: string) => (i === "1" ? "2025" : "2023"))
      .style("font-size", config.yearFontSize)
      .style("font-weight", "bold");
    years
      .append("line")
      .attr("y1", 3)
      .attr("y2", 9)
      .attr("stroke", "currentColor");

    // Crear grupos - estos son CONSTANTES y no cambiarán
    const linesGroup = svg.append("g").attr("class", "lines");
    const diffLabelsGroup = svg.append("g").attr("class", "diff-labels");
    const pointsGroup = svg.append("g").attr("class", "points");
    const labels2023Group = svg
      .append("g")
      .attr("class", "labels-2023")
      .attr("transform", `translate(${x("0")! - config.padding},0)`)
      .attr("text-anchor", "end");
    const labels2025Group = svg
      .append("g")
      .attr("class", "labels-2025")
      .attr("transform", `translate(${x("1")! + config.padding},0)`)
      .attr("text-anchor", "start");

    // Función de actualización que usa las referencias de grupos de este SVG específico
    function update(data: any[]) {
      // Actualizar dominio Y
      y.domain([
        0,
        Math.max(
          30,
          d3.max(
            data.flatMap((d) =>
              [d["2023"], d["2025"]].filter((v) => v != null),
            ),
          )! + 3,
        ),
      ]);

      // Actualizar línea base
      baseLine.transition().duration(500).attr("y1", y(0)).attr("y2", y(0));

      // Procesar datos
      const lineData = data.filter(
        (d) => d["2023"] != null && d["2025"] != null,
      );

      const pointData = data.flatMap((d) => {
        const points = [];
        if (d["2023"] != null)
          points.push({
            id: `${d.name}-2023`,
            name: d.name,
            value: d["2023"],
            year: "0",
            color: d.color,
            originalData: d,
          });
        if (d["2025"] != null)
          points.push({
            id: `${d.name}-2025`,
            name: d.name,
            value: d["2025"],
            year: "1",
            color: d.color,
            originalData: d,
          });
        return points;
      });

      const diffLabelDataTemp = lineData.map((d) => {
        const x1 = x("0")!,
          y1 = y(d["2023"]),
          x2 = x("1")!,
          y2 = y(d["2025"]);
        const midX = (x1 + x2) / 2,
          midY = (y1 + y2) / 2;
        const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI);
        const diff = (d["2025"] as number) - (d["2023"] as number);
        const diffText = `${diff > 0 ? "+" : ""}${formatNumberNoPct(diff)}`;
        const perpAngle = Math.atan2(y2 - y1, x2 - x1) + Math.PI / 2;
        const offsetX = config.diffLabelOffset * Math.cos(perpAngle);
        const offsetY = config.diffLabelOffset * Math.sin(perpAngle);
        return {
          id: `diff-${d.name}`,
          x: midX + offsetX,
          y: midY + offsetY,
          angle,
          text: diffText,
          color: d.color,
        };
      });

      const diffOffsets = resolveDiffLabelCollisions(diffLabelDataTemp);
      const diffLabelData = diffLabelDataTemp.map((d, i) => ({
        ...d,
        dx: diffOffsets[i],
      }));

      const labels2023 = data.filter((d) => d["2023"] != null);
      const positions2023 = dodge(labels2023.map((d) => y(d["2023"])));
      const labelData2023 = (
        d3.zip(labels2023, positions2023) as [any, any][]
      ).map(([d, yPos]) => ({
        id: `label-${d.name}-2023`,
        data: d,
        yPos,
        name: d.name,
        value: formatNumber(d["2023"] as number),
      }));

      const labels2025 = data.filter((d) => d["2025"] != null);
      const positions2025 = dodge(labels2025.map((d) => y(d["2025"])));
      const labelData2025 = (
        d3.zip(labels2025, positions2025) as [any, any][]
      ).map(([d, yPos]) => ({
        id: `label-${d.name}-2025`,
        data: d,
        yPos,
        name: d.name2 || d.name,
        value: formatNumber(d["2025"] as number),
      }));

      // Update líneas
      linesGroup
        .selectAll("path")
        .data(lineData, (d: any) => d.name)
        .join(
          (enter) =>
            enter
              .append("path")
              .attr("fill", "none")
              .attr("stroke-width", config.lineWidth)
              .attr("stroke", (d: any) => d.color)
              .attr("d", createLine)
              .attr("opacity", 0)
              .call((enter) =>
                enter.transition().duration(500).attr("opacity", 1),
              ),
          (update) =>
            update.call((update) =>
              update
                .transition()
                .duration(500)
                .attr("stroke", (d: any) => d.color)
                .attr("d", createLine),
            ),
          (exit) =>
            exit.call((exit) =>
              exit.transition().duration(500).attr("opacity", 0).remove(),
            ),
        );

      // Update etiquetas diferencia
      diffLabelsGroup
        .selectAll("text")
        .data(diffLabelData, (d: any) => d.id)
        .join(
          (enter) =>
            enter
              .append("text")
              .attr("x", (d: any) => d.x)
              .attr("y", (d: any) => d.y)
              .attr("dx", (d: any) => d.dx)
              .attr("text-anchor", "middle")
              .attr("dominant-baseline", "middle")
              .attr(
                "transform",
                (d: any) => `rotate(${d.angle}, ${d.x}, ${d.y})`,
              )
              .attr("fill", (d: any) => d.color)
              .attr("stroke", "white")
              .attr("stroke-width", config.diffLabelStrokeWidth)
              .attr("paint-order", "stroke fill")
              .style("font-size", config.diffLabelFontSize)
              .style("font-weight", "bold")
              .text((d: any) => d.text)
              .attr("opacity", 0)
              .call((enter) =>
                enter.transition().duration(500).delay(300).attr("opacity", 1),
              ),
          (update) =>
            update.call((update) =>
              update
                .transition()
                .duration(500)
                .attr("x", (d: any) => d.x)
                .attr("y", (d: any) => d.y)
                .attr("dx", (d: any) => d.dx)
                .attr(
                  "transform",
                  (d: any) => `rotate(${d.angle}, ${d.x}, ${d.y})`,
                )
                .attr("fill", (d: any) => d.color)
                .text((d: any) => d.text),
            ),
          (exit) =>
            exit.call((exit) =>
              exit.transition().duration(500).attr("opacity", 0).remove(),
            ),
        );

      // Update puntos
      pointsGroup
        .selectAll("circle")
        .data(pointData, (d: any) => d.id)
        .join(
          (enter) =>
            enter
              .append("circle")
              .attr("cx", (d: any) => x(d.year)!)
              .attr("cy", (d: any) => y(d.value))
              .attr("r", 0)
              .attr("fill", (d: any) => d.color)
              .call((enter) =>
                enter
                  .transition()
                  .duration(500)
                  .delay(200)
                  .attr("r", config.circleRadius),
              ),
          (update) =>
            update.call((update) =>
              update
                .transition()
                .duration(500)
                .attr("cx", (d: any) => x(d.year)!)
                .attr("cy", (d: any) => y(d.value))
                .attr("fill", (d: any) => d.color)
                .attr("r", config.circleRadius),
            ),
          (exit) =>
            exit.call((exit) =>
              exit.transition().duration(500).attr("r", 0).remove(),
            ),
        );

      // Update labels 2023
      labels2023Group
        .selectAll("text")
        .data(labelData2023, (d: any) => d.id)
        .join(
          (enter) => {
            const textEnter = enter
              .append("text")
              .attr("y", (d: any) => d.yPos)
              .attr("dy", "0.35em")
              .attr("fill", (d: any) => d.data.color)
              .style("font-size", config.labelFontSize)
              .attr("opacity", 0);
            textEnter
              .append("tspan")
              .attr("font-weight", "bold")
              .text((d: any) => d.name);
            textEnter.append("tspan").text((d: any) => `: ${d.value}`);
            return textEnter.call((enter) =>
              enter.transition().duration(500).delay(400).attr("opacity", 1),
            );
          },
          (update) =>
            update.call((update) => {
              update
                .transition()
                .duration(500)
                .attr("y", (d: any) => d.yPos)
                .attr("fill", (d: any) => d.data.color);
              update.select("tspan:nth-child(1)").text((d: any) => d.name);
              update
                .select("tspan:nth-child(2)")
                .text((d: any) => `: ${d.value}`);
              return update;
            }),
          (exit) =>
            exit.call((exit) =>
              exit.transition().duration(500).attr("opacity", 0).remove(),
            ),
        );

      // Update labels 2025
      labels2025Group
        .selectAll("text")
        .data(labelData2025, (d: any) => d.id)
        .join(
          (enter) => {
            const textEnter = enter
              .append("text")
              .attr("y", (d: any) => d.yPos)
              .attr("dy", "0.35em")
              .attr("fill", (d: any) => d.data.color)
              .style("font-size", config.labelFontSize)
              .attr("opacity", 0);
            textEnter
              .append("tspan")
              .attr("font-weight", "bold")
              .text((d: any) => d.name);
            textEnter.append("tspan").text((d: any) => `: ${d.value}`);
            return textEnter.call((enter) =>
              enter.transition().duration(500).delay(400).attr("opacity", 1),
            );
          },
          (update) =>
            update.call((update) => {
              update
                .transition()
                .duration(500)
                .attr("y", (d: any) => d.yPos)
                .attr("fill", (d: any) => d.data.color);
              update.select("tspan:nth-child(1)").text((d: any) => d.name);
              update
                .select("tspan:nth-child(2)")
                .text((d: any) => `: ${d.value}`);
              return update;
            }),
          (exit) =>
            exit.call((exit) =>
              exit.transition().duration(500).attr("opacity", 0).remove(),
            ),
        );
    }

    // Render con datos agregados
    update(chartData as any[]);
  }, [comparison]);

  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="h-full w-full max-w-full">
        <svg ref={svgRef} style={{ height: "100%", margin: "auto" }} />
      </div>
    </div>
  );
}
